'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { type VariantProps, cva } from 'class-variance-authority'
import { useId } from 'react'
import type { IOptions } from 'sanitize-html'
import sanitizeHtml from 'sanitize-html'

export const calloutTintVariants = {
  default: 'border-brand-2-800 text-brand-2-900',
  primary: 'border-primary text-primary',
  secondary: 'border-secondary text-secondary',
  info: 'border-info-fg text-info-fg',
  success: 'border-success-fg text-success-fg',
  warning: 'border-warning-fg text-warning-fg',
  error: 'border-error-fg text-error-fg',
  'click-and-collect': 'border-click-and-collect-fg text-click-and-collect-fg',
}

export const calloutCva = cva(
  'flex w-full items-center gap-3 rounded-sm border-l-[0.375rem] border-r-[0.375rem] border-solid bg-white py-2 shadow-callout desktop:p-3',
  {
    variants: {
      tint: calloutTintVariants,
    },
  },
)

type CalloutVariantProps = VariantProps<typeof calloutCva>

export type CalloutTint = NonNullable<CalloutVariantProps['tint']>

export type CalloutProps = UiComponentProps<{
  /** The semantic tint of the callout - drives the border/icon/text color. */
  tint: CalloutTint
  /** Title, sanitized as HTML. Also used as the component's accessible name unless `aria-labelledby` is provided. */
  title: string
  /** Whether the title renders bold. Defaults to `true`. */
  titleWeight?: boolean
  /** Icon or Illustration rendered before the title, always forced to 24px by Callout itself. */
  icon?: React.ReactNode
  /** A `string` is sanitized as HTML; any other `ReactNode` is rendered as-is, unsanitized. */
  description?: React.ReactNode
  /** Trailing control slot, e.g. a `<Toggle/>`. */
  toggle?: React.ReactNode
  /** Custom sanitize options (if not provided, default options will be used) */
  sanitizeOptions?: IOptions
  children?: React.ReactNode
}> &
  WithPayloadBlockId & {
    /** Overrides the default accessible name (the rendered title) with an external heading id. */
    'aria-labelledby'?: string
  }

export function Callout({
  tint,
  title,
  titleWeight = true,
  icon,
  description,
  toggle,
  children,
  id,
  className,
  sanitizeOptions,
  'data-testid': dataTestId = 'callout',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'data-payload-block-id': dataPayloadBlockId,
}: CalloutProps) {
  const calloutId = useId()
  const titleId = id ? `${id}-title` : `callout-title-${calloutId}`
  const labelledBy = ariaLabelledBy ?? (ariaLabel ? undefined : titleId)

  return (
    <div
      id={id}
      role="status"
      aria-live={tint === 'error' ? 'assertive' : 'polite'}
      aria-label={ariaLabel}
      aria-labelledby={labelledBy}
      className={twMerge(className, calloutCva({ tint }))}
      data-testid={dataTestId}
      data-payload-block-id={dataPayloadBlockId}
    >
      {icon && (
        <div
          className="flex shrink-0 items-center justify-center [&_svg]:size-6"
          data-testid={`${dataTestId}/icon`}
        >
          {icon}
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1 [&_li::marker]:text-[10px] [&_ul]:ml-2 [&_ul]:list-disc">
        <p
          id={titleId}
          className={twMerge('text-lg', titleWeight && 'font-bold')}
          data-testid={`${dataTestId}/title`}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(title) }}
        />

        {description !== undefined && (
          <div
            className="text-black"
            data-testid={`${dataTestId}/description`}
            {...(typeof description === 'string'
              ? { dangerouslySetInnerHTML: { __html: sanitizeHtml(description, sanitizeOptions) } }
              : { children: description })}
          />
        )}

        {children}
      </div>

      {toggle && (
        <div className="flex shrink-0 items-center" data-testid={`${dataTestId}/toggle`}>
          {toggle}
        </div>
      )}
    </div>
  )
}
